import { getApiBaseUrl } from '../../auth/api';
import { authFetch } from '../../auth/authFetch';
import type {
  ApiResponse,
  AdjustmentOptions,
  BusinessRecordReference,
  CreatePayRunRequest,
  PayGroupReference,
  PayPeriodReference,
  PayRunApiError,
  PayRunDetailDto,
  PayRunFinalisationDto,
  PayAdvicePreparationSummaryDto,
  PayRunCalculationSummaryDto,
  PayRunApprovalDecision,
  PayRunApprovalDto,
  PayRunFieldError,
  PayRunListQuery,
  PayRunListResult,
  PayRunType,
  UpdatePayRunRequest,
} from './payRunsApiTypes';

export class PayRunRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly fieldErrors: PayRunFieldError[],
  ) {
    super(message);
    this.name = 'PayRunRequestError';
  }
}

const base = () => `${getApiBaseUrl()}/api/payrun`;

const parse = async <T>(response: Response): Promise<T> => {
  let body: ApiResponse<T>;
  try {
    body = await response.json() as ApiResponse<T>;
  } catch {
    throw new PayRunRequestError(response.status, 'PAYRUN_INTERNAL_ERROR', `Request failed: ${response.status}`, []);
  }

  if (!response.ok || !body.success || body.result == null) {
    // The API sends a failure's detail in `result`; `error` is read as well for older shapes.
    const detail = (body.success ? null : body.result as PayRunApiError | null | undefined) ?? body.error;
    throw new PayRunRequestError(
      response.status,
      detail?.code ?? (response.status === 403 ? 'PAYRUN_FORBIDDEN' : 'PAYRUN_INTERNAL_ERROR'),
      body.message || `Request failed: ${response.status}`,
      detail?.fieldErrors ?? [],
    );
  }

  return body.result;
};

const setOptional = (params: URLSearchParams, key: string, value?: string) => {
  if (value?.trim()) params.set(key, value.trim());
};

export const listPayRuns = async (
  query: PayRunListQuery,
  signal?: AbortSignal,
): Promise<PayRunListResult> => {
  const params = new URLSearchParams({
    scope: query.scope,
    page: String(query.page),
    pageSize: String(query.pageSize),
    sort: query.sort,
  });
  setOptional(params, 'search', query.search);
  setOptional(params, 'businessRecordId', query.businessRecordId);
  setOptional(params, 'payGroupId', query.payGroupId);
  setOptional(params, 'workflowStatus', query.workflowStatus);
  setOptional(params, 'frequency', query.frequency);
  return parse<PayRunListResult>(await authFetch(`${base()}?${params}`, { signal }));
};

export const listBusinessRecordReferences = async (): Promise<BusinessRecordReference[]> =>
  parse<BusinessRecordReference[]>(await authFetch(`${base()}/reference/business-records`));

export const listPayGroupReferences = async (businessRecordId: string): Promise<PayGroupReference[]> =>
  parse<PayGroupReference[]>(await authFetch(
    `${base()}/reference/pay-groups?${new URLSearchParams({ businessRecordId })}`,
  ));

export const listPayPeriodReferences = async (
  businessRecordId: string,
  payGroupId: string,
  runType: PayRunType,
): Promise<PayPeriodReference[]> =>
  parse<PayPeriodReference[]>(await authFetch(
    `${base()}/reference/pay-periods?${new URLSearchParams({ businessRecordId, payGroupId, runType })}`,
  ));

export const getAdjustmentOptions = async (
  businessRecordId: string,
  payGroupId: string,
): Promise<AdjustmentOptions> =>
  parse<AdjustmentOptions>(await authFetch(
    `${base()}/reference/adjustment-options?${new URLSearchParams({ businessRecordId, payGroupId })}`,
  ));

export const createPayRun = async (
  request: CreatePayRunRequest,
  idempotencyKey: string,
): Promise<PayRunDetailDto> =>
  parse<PayRunDetailDto>(await authFetch(base(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Idempotency-Key': idempotencyKey,
    },
    body: JSON.stringify(request),
  }));

export const updatePayRun = async (
  payId: string,
  request: UpdatePayRunRequest,
): Promise<PayRunDetailDto> =>
  parse<PayRunDetailDto>(await authFetch(`${base()}/${encodeURIComponent(payId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  }, { retryUnauthorized: false }));

export const transitionPayRun = async (
  payId: string,
  verb: 'make_payroll_active' | 'revert_to_trial' | 'unlock_to_compliance' | 'cancel',
): Promise<PayRunDetailDto> =>
  parse<PayRunDetailDto>(await authFetch(`${base()}/${encodeURIComponent(payId)}/transition`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ verb }),
  }, { retryUnauthorized: false }));

export const finalisePayRun = async (payId: string): Promise<PayRunFinalisationDto> =>
  parse<PayRunFinalisationDto>(await authFetch(`${base()}/${encodeURIComponent(payId)}/finalisation`, {
    method: 'POST',
  }, { retryUnauthorized: false }));

export const getPayRunFinalisation = async (
  payId: string,
  signal?: AbortSignal,
): Promise<PayRunFinalisationDto> =>
  parse<PayRunFinalisationDto>(await authFetch(
    `${base()}/${encodeURIComponent(payId)}/finalisation`,
    { signal },
  ));

export const getPayAdvicePreparation = async (payId: string, signal?: AbortSignal): Promise<PayAdvicePreparationSummaryDto> =>
  parse<PayAdvicePreparationSummaryDto>(await authFetch(
    `${base()}/${encodeURIComponent(payId)}/pay-advice-preparation`, { signal },
  ));

export const retryPayAdvicePreparation = async (payId: string): Promise<PayAdvicePreparationSummaryDto> =>
  parse<PayAdvicePreparationSummaryDto>(await authFetch(
    `${base()}/${encodeURIComponent(payId)}/pay-advice-preparation/retry`,
    { method: 'POST' }, { retryUnauthorized: false },
  ));

/**
 * PRR-317 interim ABA download. The response body is the ABA file itself rather than the JSON envelope, so this
 * deliberately bypasses `parse` and verifies success before the body is ever treated as ABA content. Failures
 * still arrive as the ordinary JSON error contract and are surfaced as a normal `PayRunRequestError`.
 */
export const downloadPayRunAbaFile = async (
  businessRecordId: string,
  payId: string,
): Promise<{ blob: Blob; fileName: string }> => {
  const response = await authFetch(
    `${getApiBaseUrl()}/api/business-records/${encodeURIComponent(businessRecordId)}/payrun/${encodeURIComponent(payId)}/aba`,
  );

  if (!response.ok) {
    let code = response.status === 403 ? 'PAYRUN_FORBIDDEN' : 'PAYRUN_INTERNAL_ERROR';
    let message = `Request failed: ${response.status}`;
    try {
      const body = await response.json() as ApiResponse<unknown>;
      code = body.error?.code ?? code;
      message = body.message || message;
    } catch {
      // A non-JSON failure body is never shown to the operator or treated as ABA content.
    }
    throw new PayRunRequestError(response.status, code, message, []);
  }

  return { blob: await response.blob(), fileName: abaFileNameFrom(response) };
};

const abaFileNameFrom = (response: Response): string => {
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const quoted = /filename="([^"]+)"/i.exec(disposition)?.[1];
  const bare = /filename=([^;]+)/i.exec(disposition)?.[1]?.trim();
  const candidate = (quoted ?? bare ?? '').trim();
  // Never let a server-supplied name introduce a path or an unexpected extension.
  const safe = candidate.split(/[\\/]/).pop() ?? '';
  return /^[A-Za-z0-9._-]+\.aba$/.test(safe) ? safe : 'pay-run.aba';
};

export const retryPayRunFanOutLeg = async (
  payId: string,
  legType: string,
): Promise<PayRunFinalisationDto> =>
  parse<PayRunFinalisationDto>(await authFetch(
    `${base()}/${encodeURIComponent(payId)}/finalisation/legs/${encodeURIComponent(legType)}/retry`,
    { method: 'POST' },
    { retryUnauthorized: false },
  ));

export const recoverPayRunFinalisation = async (payId: string): Promise<PayRunFinalisationDto> =>
  parse<PayRunFinalisationDto>(await authFetch(
    `${base()}/${encodeURIComponent(payId)}/finalisation/recover`,
    { method: 'POST' },
    { retryUnauthorized: false },
  ));

export const getPayRunApproval = async (
  payId: string,
  signal?: AbortSignal,
): Promise<PayRunApprovalDto> =>
  parse<PayRunApprovalDto>(await authFetch(`${base()}/${encodeURIComponent(payId)}/approval`, { signal }));

export const submitPayRunApproval = async (
  payId: string,
  expectedCalculationHash: string,
): Promise<PayRunApprovalDto> =>
  parse<PayRunApprovalDto>(await authFetch(`${base()}/${encodeURIComponent(payId)}/approval/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ expectedCalculationHash }),
  }, { retryUnauthorized: false }));

export const decidePayRunApproval = async (
  payId: string,
  requirementId: string,
  decision: PayRunApprovalDecision,
  expectedCalculationHash: string,
  comment?: string,
): Promise<PayRunApprovalDto> =>
  parse<PayRunApprovalDto>(await authFetch(
    `${base()}/${encodeURIComponent(payId)}/approval/requirements/${encodeURIComponent(requirementId)}/decision`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ decision, expectedCalculationHash, comment: comment?.trim() || null }),
    },
    { retryUnauthorized: false },
  ));

export const startPayRunCalculation = async (
  payId: string,
  idempotencyKey: string,
): Promise<PayRunCalculationSummaryDto> =>
  parse<PayRunCalculationSummaryDto>(await authFetch(
    `${base()}/${encodeURIComponent(payId)}/calculations`,
    {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
    },
    { retryUnauthorized: false },
  ));

export const getPayRunCalculation = async (
  payId: string,
  attemptId: string,
  signal?: AbortSignal,
): Promise<PayRunCalculationSummaryDto> =>
  parse<PayRunCalculationSummaryDto>(await authFetch(
    `${base()}/${encodeURIComponent(payId)}/calculations/${encodeURIComponent(attemptId)}`,
    { signal },
  ));

export const retryPayRunCalculation = async (
  payId: string,
  attemptId: string,
): Promise<PayRunCalculationSummaryDto> =>
  parse<PayRunCalculationSummaryDto>(await authFetch(
    `${base()}/${encodeURIComponent(payId)}/calculations/${encodeURIComponent(attemptId)}/retry`,
    { method: 'POST' },
    { retryUnauthorized: false },
  ));
